import { MitreTactic } from '../types/mitreHeatmap';

/**
 * MITRE ATT&CK Enterprise Matrix v16
 * Reference: https://attack.mitre.org/matrices/enterprise/
 *
 * Contains all 14 Enterprise tactics with their parent-level techniques.
 * Sub-techniques (e.g. T1566.001) are NOT included — only top-level technique IDs.
 */
export const MITRE_ATTACK_ENTERPRISE: MitreTactic[] = [
  // ───────────────────────────────────────────────
  // 1. TA0043 — Reconnaissance
  // ───────────────────────────────────────────────
  {
    id: 'TA0043',
    name: 'Reconnaissance',
    shortName: 'reconnaissance',
    techniques: [
      { id: 'T1595', name: 'Active Scanning' },
      { id: 'T1592', name: 'Gather Victim Host Information' },
      { id: 'T1589', name: 'Gather Victim Identity Information' },
      { id: 'T1590', name: 'Gather Victim Network Information' },
      { id: 'T1591', name: 'Gather Victim Org Information' },
      { id: 'T1598', name: 'Phishing for Information' },
      { id: 'T1597', name: 'Search Closed Sources' },
      { id: 'T1596', name: 'Search Open Technical Databases' },
      { id: 'T1593', name: 'Search Open Websites/Domains' },
      { id: 'T1594', name: 'Search Victim-Owned Websites' },
    ],
  },

  // ───────────────────────────────────────────────
  // 2. TA0042 — Resource Development
  // ───────────────────────────────────────────────
  {
    id: 'TA0042',
    name: 'Resource Development',
    shortName: 'resource-development',
    techniques: [
      { id: 'T1583', name: 'Acquire Infrastructure' },
      { id: 'T1586', name: 'Compromise Accounts' },
      { id: 'T1584', name: 'Compromise Infrastructure' },
      { id: 'T1587', name: 'Develop Capabilities' },
      { id: 'T1585', name: 'Establish Accounts' },
      { id: 'T1588', name: 'Obtain Capabilities' },
      { id: 'T1608', name: 'Stage Capabilities' },
    ],
  },

  // ───────────────────────────────────────────────
  // 3. TA0001 — Initial Access
  // ───────────────────────────────────────────────
  {
    id: 'TA0001',
    name: 'Initial Access',
    shortName: 'initial-access',
    techniques: [
      { id: 'T1659', name: 'Content Injection' },
      { id: 'T1189', name: 'Drive-by Compromise' },
      { id: 'T1190', name: 'Exploit Public-Facing Application' },
      { id: 'T1133', name: 'External Remote Services' },
      { id: 'T1200', name: 'Hardware Additions' },
      { id: 'T1566', name: 'Phishing' },
      { id: 'T1091', name: 'Replication Through Removable Media' },
      { id: 'T1195', name: 'Supply Chain Compromise' },
      { id: 'T1199', name: 'Trusted Relationship' },
      { id: 'T1078', name: 'Valid Accounts' },
    ],
  },

  // ───────────────────────────────────────────────
  // 4. TA0002 — Execution
  // ───────────────────────────────────────────────
  {
    id: 'TA0002',
    name: 'Execution',
    shortName: 'execution',
    techniques: [
      { id: 'T1651', name: 'Cloud Administration Command' },
      { id: 'T1059', name: 'Command and Scripting Interpreter' },
      { id: 'T1609', name: 'Container Administration Command' },
      { id: 'T1610', name: 'Deploy Container' },
      { id: 'T1203', name: 'Exploitation for Client Execution' },
      { id: 'T1559', name: 'Inter-Process Communication' },
      { id: 'T1106', name: 'Native API' },
      { id: 'T1053', name: 'Scheduled Task/Job' },
      { id: 'T1648', name: 'Serverless Execution' },
      { id: 'T1129', name: 'Shared Modules' },
      { id: 'T1072', name: 'Software Deployment Tools' },
      { id: 'T1569', name: 'System Services' },
      { id: 'T1204', name: 'User Execution' },
      { id: 'T1047', name: 'Windows Management Instrumentation' },
    ],
  },

  // ───────────────────────────────────────────────
  // 5. TA0003 — Persistence
  // ───────────────────────────────────────────────
  {
    id: 'TA0003',
    name: 'Persistence',
    shortName: 'persistence',
    techniques: [
      { id: 'T1098', name: 'Account Manipulation' },
      { id: 'T1197', name: 'BITS Jobs' },
      { id: 'T1547', name: 'Boot or Logon Autostart Execution' },
      { id: 'T1037', name: 'Boot or Logon Initialization Scripts' },
      { id: 'T1176', name: 'Browser Extensions' },
      { id: 'T1554', name: 'Compromise Client Software Binary' },
      { id: 'T1136', name: 'Create Account' },
      { id: 'T1543', name: 'Create or Modify System Process' },
      { id: 'T1546', name: 'Event Triggered Execution' },
      { id: 'T1133', name: 'External Remote Services' },
      { id: 'T1574', name: 'Hijack Execution Flow' },
      { id: 'T1525', name: 'Implant Internal Image' },
      { id: 'T1556', name: 'Modify Authentication Process' },
      { id: 'T1137', name: 'Office Application Startup' },
      { id: 'T1653', name: 'Power Settings' },
      { id: 'T1542', name: 'Pre-OS Boot' },
      { id: 'T1053', name: 'Scheduled Task/Job' },
      { id: 'T1505', name: 'Server Software Component' },
      { id: 'T1205', name: 'Traffic Signaling' },
      { id: 'T1078', name: 'Valid Accounts' },
    ],
  },

  // ───────────────────────────────────────────────
  // 6. TA0004 — Privilege Escalation
  // ───────────────────────────────────────────────
  {
    id: 'TA0004',
    name: 'Privilege Escalation',
    shortName: 'privilege-escalation',
    techniques: [
      { id: 'T1548', name: 'Abuse Elevation Control Mechanism' },
      { id: 'T1134', name: 'Access Token Manipulation' },
      { id: 'T1547', name: 'Boot or Logon Autostart Execution' },
      { id: 'T1037', name: 'Boot or Logon Initialization Scripts' },
      { id: 'T1543', name: 'Create or Modify System Process' },
      { id: 'T1484', name: 'Domain or Tenant Policy Modification' },
      { id: 'T1611', name: 'Escape to Host' },
      { id: 'T1546', name: 'Event Triggered Execution' },
      { id: 'T1068', name: 'Exploitation for Privilege Escalation' },
      { id: 'T1574', name: 'Hijack Execution Flow' },
      { id: 'T1055', name: 'Process Injection' },
      { id: 'T1053', name: 'Scheduled Task/Job' },
      { id: 'T1078', name: 'Valid Accounts' },
    ],
  },

  // ───────────────────────────────────────────────
  // 7. TA0005 — Defense Evasion
  // ───────────────────────────────────────────────
  {
    id: 'TA0005',
    name: 'Defense Evasion',
    shortName: 'defense-evasion',
    techniques: [
      { id: 'T1548', name: 'Abuse Elevation Control Mechanism' },
      { id: 'T1134', name: 'Access Token Manipulation' },
      { id: 'T1197', name: 'BITS Jobs' },
      { id: 'T1612', name: 'Build Image on Host' },
      { id: 'T1622', name: 'Debugger Evasion' },
      { id: 'T1140', name: 'Deobfuscate/Decode Files or Information' },
      { id: 'T1610', name: 'Deploy Container' },
      { id: 'T1006', name: 'Direct Volume Access' },
      { id: 'T1484', name: 'Domain or Tenant Policy Modification' },
      { id: 'T1480', name: 'Execution Guardrails' },
      { id: 'T1211', name: 'Exploitation for Defense Evasion' },
      { id: 'T1222', name: 'File and Directory Permissions Modification' },
      { id: 'T1564', name: 'Hide Artifacts' },
      { id: 'T1574', name: 'Hijack Execution Flow' },
      { id: 'T1562', name: 'Impair Defenses' },
      { id: 'T1656', name: 'Impersonation' },
      { id: 'T1070', name: 'Indicator Removal' },
      { id: 'T1202', name: 'Indirect Command Execution' },
      { id: 'T1036', name: 'Masquerading' },
      { id: 'T1556', name: 'Modify Authentication Process' },
      { id: 'T1578', name: 'Modify Cloud Compute Infrastructure' },
      { id: 'T1112', name: 'Modify Registry' },
      { id: 'T1601', name: 'Modify System Image' },
      { id: 'T1599', name: 'Network Boundary Bridging' },
      { id: 'T1027', name: 'Obfuscated Files or Information' },
      { id: 'T1647', name: 'Plist File Modification' },
      { id: 'T1542', name: 'Pre-OS Boot' },
      { id: 'T1055', name: 'Process Injection' },
      { id: 'T1620', name: 'Reflective Code Loading' },
      { id: 'T1207', name: 'Rogue Domain Controller' },
      { id: 'T1014', name: 'Rootkit' },
      { id: 'T1553', name: 'Subvert Trust Controls' },
      { id: 'T1218', name: 'System Binary Proxy Execution' },
      { id: 'T1216', name: 'System Script Proxy Execution' },
      { id: 'T1221', name: 'Template Injection' },
      { id: 'T1205', name: 'Traffic Signaling' },
      { id: 'T1127', name: 'Trusted Developer Utilities Proxy Execution' },
      { id: 'T1535', name: 'Unused/Unsupported Cloud Regions' },
      { id: 'T1550', name: 'Use Alternate Authentication Material' },
      { id: 'T1078', name: 'Valid Accounts' },
      { id: 'T1497', name: 'Virtualization/Sandbox Evasion' },
      { id: 'T1600', name: 'Weaken Encryption' },
      { id: 'T1220', name: 'XSL Script Processing' },
    ],
  },

  // ───────────────────────────────────────────────
  // 8. TA0006 — Credential Access
  // ───────────────────────────────────────────────
  {
    id: 'TA0006',
    name: 'Credential Access',
    shortName: 'credential-access',
    techniques: [
      { id: 'T1557', name: 'Adversary-in-the-Middle' },
      { id: 'T1110', name: 'Brute Force' },
      { id: 'T1555', name: 'Credentials from Password Stores' },
      { id: 'T1212', name: 'Exploitation for Credential Access' },
      { id: 'T1187', name: 'Forced Authentication' },
      { id: 'T1606', name: 'Forge Web Credentials' },
      { id: 'T1056', name: 'Input Capture' },
      { id: 'T1556', name: 'Modify Authentication Process' },
      { id: 'T1111', name: 'Multi-Factor Authentication Interception' },
      { id: 'T1621', name: 'Multi-Factor Authentication Request Generation' },
      { id: 'T1040', name: 'Network Sniffing' },
      { id: 'T1003', name: 'OS Credential Dumping' },
      { id: 'T1528', name: 'Steal Application Access Token' },
      { id: 'T1649', name: 'Steal or Forge Authentication Certificates' },
      { id: 'T1558', name: 'Steal or Forge Kerberos Tickets' },
      { id: 'T1539', name: 'Steal Web Session Cookie' },
      { id: 'T1552', name: 'Unsecured Credentials' },
    ],
  },

  // ───────────────────────────────────────────────
  // 9. TA0007 — Discovery
  // ───────────────────────────────────────────────
  {
    id: 'TA0007',
    name: 'Discovery',
    shortName: 'discovery',
    techniques: [
      { id: 'T1087', name: 'Account Discovery' },
      { id: 'T1010', name: 'Application Window Discovery' },
      { id: 'T1217', name: 'Browser Information Discovery' },
      { id: 'T1580', name: 'Cloud Infrastructure Discovery' },
      { id: 'T1538', name: 'Cloud Service Dashboard' },
      { id: 'T1526', name: 'Cloud Service Discovery' },
      { id: 'T1613', name: 'Container and Resource Discovery' },
      { id: 'T1622', name: 'Debugger Evasion' },
      { id: 'T1652', name: 'Device Driver Discovery' },
      { id: 'T1482', name: 'Domain Trust Discovery' },
      { id: 'T1083', name: 'File and Directory Discovery' },
      { id: 'T1615', name: 'Group Policy Discovery' },
      { id: 'T1654', name: 'Log Enumeration' },
      { id: 'T1046', name: 'Network Service Discovery' },
      { id: 'T1135', name: 'Network Share Discovery' },
      { id: 'T1040', name: 'Network Sniffing' },
      { id: 'T1201', name: 'Password Policy Discovery' },
      { id: 'T1120', name: 'Peripheral Device Discovery' },
      { id: 'T1069', name: 'Permission Groups Discovery' },
      { id: 'T1057', name: 'Process Discovery' },
      { id: 'T1012', name: 'Query Registry' },
      { id: 'T1018', name: 'Remote System Discovery' },
      { id: 'T1518', name: 'Software Discovery' },
      { id: 'T1082', name: 'System Information Discovery' },
      { id: 'T1614', name: 'System Location Discovery' },
      { id: 'T1016', name: 'System Network Configuration Discovery' },
      { id: 'T1049', name: 'System Network Connections Discovery' },
      { id: 'T1033', name: 'System Owner/User Discovery' },
      { id: 'T1007', name: 'System Service Discovery' },
      { id: 'T1124', name: 'System Time Discovery' },
      { id: 'T1497', name: 'Virtualization/Sandbox Evasion' },
    ],
  },

  // ───────────────────────────────────────────────
  // 10. TA0008 — Lateral Movement
  // ───────────────────────────────────────────────
  {
    id: 'TA0008',
    name: 'Lateral Movement',
    shortName: 'lateral-movement',
    techniques: [
      { id: 'T1210', name: 'Exploitation of Remote Services' },
      { id: 'T1534', name: 'Internal Spearphishing' },
      { id: 'T1570', name: 'Lateral Tool Transfer' },
      { id: 'T1563', name: 'Remote Service Session Hijacking' },
      { id: 'T1021', name: 'Remote Services' },
      { id: 'T1091', name: 'Replication Through Removable Media' },
      { id: 'T1072', name: 'Software Deployment Tools' },
      { id: 'T1080', name: 'Taint Shared Content' },
      { id: 'T1550', name: 'Use Alternate Authentication Material' },
    ],
  },

  // ───────────────────────────────────────────────
  // 11. TA0009 — Collection
  // ───────────────────────────────────────────────
  {
    id: 'TA0009',
    name: 'Collection',
    shortName: 'collection',
    techniques: [
      { id: 'T1557', name: 'Adversary-in-the-Middle' },
      { id: 'T1560', name: 'Archive Collected Data' },
      { id: 'T1123', name: 'Audio Capture' },
      { id: 'T1119', name: 'Automated Collection' },
      { id: 'T1185', name: 'Browser Session Hijacking' },
      { id: 'T1115', name: 'Clipboard Data' },
      { id: 'T1530', name: 'Data from Cloud Storage' },
      { id: 'T1602', name: 'Data from Configuration Repository' },
      { id: 'T1213', name: 'Data from Information Repositories' },
      { id: 'T1005', name: 'Data from Local System' },
      { id: 'T1039', name: 'Data from Network Shared Drive' },
      { id: 'T1025', name: 'Data from Removable Media' },
      { id: 'T1074', name: 'Data Staged' },
      { id: 'T1114', name: 'Email Collection' },
      { id: 'T1056', name: 'Input Capture' },
      { id: 'T1113', name: 'Screen Capture' },
      { id: 'T1125', name: 'Video Capture' },
    ],
  },

  // ───────────────────────────────────────────────
  // 12. TA0011 — Command and Control
  // ───────────────────────────────────────────────
  {
    id: 'TA0011',
    name: 'Command and Control',
    shortName: 'command-and-control',
    techniques: [
      { id: 'T1071', name: 'Application Layer Protocol' },
      { id: 'T1092', name: 'Communication Through Removable Media' },
      { id: 'T1659', name: 'Content Injection' },
      { id: 'T1132', name: 'Data Encoding' },
      { id: 'T1001', name: 'Data Obfuscation' },
      { id: 'T1568', name: 'Dynamic Resolution' },
      { id: 'T1573', name: 'Encrypted Channel' },
      { id: 'T1008', name: 'Fallback Channels' },
      { id: 'T1105', name: 'Ingress Tool Transfer' },
      { id: 'T1104', name: 'Multi-Stage Channels' },
      { id: 'T1095', name: 'Non-Application Layer Protocol' },
      { id: 'T1571', name: 'Non-Standard Port' },
      { id: 'T1572', name: 'Protocol Tunneling' },
      { id: 'T1090', name: 'Proxy' },
      { id: 'T1219', name: 'Remote Access Software' },
      { id: 'T1205', name: 'Traffic Signaling' },
      { id: 'T1102', name: 'Web Service' },
    ],
  },

  // ───────────────────────────────────────────────
  // 13. TA0010 — Exfiltration
  // ───────────────────────────────────────────────
  {
    id: 'TA0010',
    name: 'Exfiltration',
    shortName: 'exfiltration',
    techniques: [
      { id: 'T1020', name: 'Automated Exfiltration' },
      { id: 'T1030', name: 'Data Transfer Size Limits' },
      { id: 'T1048', name: 'Exfiltration Over Alternative Protocol' },
      { id: 'T1041', name: 'Exfiltration Over C2 Channel' },
      { id: 'T1011', name: 'Exfiltration Over Other Network Medium' },
      { id: 'T1052', name: 'Exfiltration Over Physical Medium' },
      { id: 'T1567', name: 'Exfiltration Over Web Service' },
      { id: 'T1029', name: 'Scheduled Transfer' },
      { id: 'T1537', name: 'Transfer Data to Cloud Account' },
    ],
  },

  // ───────────────────────────────────────────────
  // 14. TA0040 — Impact
  // ───────────────────────────────────────────────
  {
    id: 'TA0040',
    name: 'Impact',
    shortName: 'impact',
    techniques: [
      { id: 'T1531', name: 'Account Access Removal' },
      { id: 'T1485', name: 'Data Destruction' },
      { id: 'T1486', name: 'Data Encrypted for Impact' },
      { id: 'T1565', name: 'Data Manipulation' },
      { id: 'T1491', name: 'Defacement' },
      { id: 'T1561', name: 'Disk Wipe' },
      { id: 'T1499', name: 'Endpoint Denial of Service' },
      { id: 'T1657', name: 'Financial Theft' },
      { id: 'T1495', name: 'Firmware Corruption' },
      { id: 'T1490', name: 'Inhibit System Recovery' },
      { id: 'T1498', name: 'Network Denial of Service' },
      { id: 'T1496', name: 'Resource Hijacking' },
      { id: 'T1489', name: 'Service Stop' },
      { id: 'T1529', name: 'System Shutdown/Reboot' },
    ],
  },
];

/**
 * Helper map: tactic shortName -> tactic ID
 * e.g. "initial-access" -> "TA0001"
 */
export const TACTIC_SHORT_NAME_TO_ID: Record<string, string> =
  MITRE_ATTACK_ENTERPRISE.reduce<Record<string, string>>((map, tactic) => {
    map[tactic.shortName] = tactic.id;
    return map;
  }, {});
